import { useRef } from "react";

import Table from "ds/components/Table";
import TableBody from "ds/components/Table/Body";
import TableSkeleton from "ds/components/Table/Skeleton";
import TableWrapper, { TableWrapperProps } from "ds/components/Table/Wrapper";

import TableSimpleHead from "./Head";
import { TableCellPerColumn, TableSimpleDataItem, TableSimpleStore } from "./types";
import { TableSettingsDrawerProps } from "../Table/Settings/DrawerContent";
import TableSimpleRows from "./Rows";
import { TableHeadInteractiveColumnProps } from "../Table/Head/InteractiveColumn";

type TableSimpleProps<T extends TableSimpleDataItem> = {
  data: T[];
  store: TableSimpleStore;
  cellPerColumn: TableCellPerColumn<T>;
  ariaLabel: string;
  loading?: boolean;
  rowsPerPage?: number;
} & Pick<TableSettingsDrawerProps, "trackAnalytics"> &
  Pick<
    TableHeadInteractiveColumnProps,
    "analyticsPage" | "filtersStorageKey" | "sortCallback" | "sortConfig"
  > &
  Pick<TableWrapperProps, "hasPagination">;

const TableSimple = <T extends TableSimpleDataItem>({
  data,
  store,
  ariaLabel,
  loading,
  rowsPerPage = 50,
  sortCallback,
  analyticsPage,
  filtersStorageKey,
  trackAnalytics,
  cellPerColumn,
  sortConfig,
  hasPagination,
}: TableSimpleProps<T>) => {
  const tableRef = useRef<HTMLDivElement>(null);

  const columnIds = store((state) => state.columnsOrder.visible);

  return (
    <TableWrapper ref={tableRef} hasPagination={hasPagination}>
      <Table ariaLabel={ariaLabel}>
        <TableSimpleHead
          store={store}
          ariaLabel={ariaLabel}
          sortCallback={sortCallback}
          analyticsPage={analyticsPage}
          filtersStorageKey={filtersStorageKey}
          trackAnalytics={trackAnalytics}
          tableRef={tableRef}
          sortConfig={sortConfig}
        />
        {loading && <TableSkeleton columnIds={columnIds} rowsPerPage={rowsPerPage} />}
        <TableBody>
          {!loading && <TableSimpleRows data={data} store={store} cellPerColumn={cellPerColumn} />}
        </TableBody>
      </Table>
    </TableWrapper>
  );
};

TableSimple.displayName = "DS.TableSimple";

export default TableSimple;
